/*
  Varelao release-safe variant integrity helper.
  It keeps Shopify's native product-form/variant-picker flow in control and only
  synchronizes an exact variant ID when all selected options can be resolved.
  Network or matching failures never disable or block purchase controls.
*/
(() => {
  const productCache = new Map();
  const normalize = (value) => String(value ?? '').trim();

  function getProductUrl(picker) {
    const raw = normalize(picker?.dataset?.productUrl);
    if (!raw) return '';
    return raw.split('?')[0].replace(/\.js$/, '');
  }

  function getProductId(picker) {
    return normalize(picker?.dataset?.productId);
  }

  function getRoot(picker) {
    return picker.closest('.shopify-section, dialog, featured-product-information, product-card') || document;
  }

  function getSelectedOptions(picker) {
    const selected = new Map();

    picker.querySelectorAll('fieldset input:checked').forEach((input) => {
      const name = normalize(input.dataset.optionName);
      if (name) selected.set(name, normalize(input.value));
    });

    picker.querySelectorAll('select').forEach((select) => {
      const option = select.selectedOptions?.[0];
      const name = normalize(option?.dataset?.optionName || select.name.replace(/^options\[|\]$/g, ''));
      if (name) selected.set(name, normalize(select.value));
    });

    picker.querySelectorAll('[data-testid="variant-option-single"]').forEach((container) => {
      const valueElement = container.querySelector('[data-testid="variant-option-single-value"]');
      const value = normalize(valueElement?.textContent);
      if (!value) return;

      const clone = container.cloneNode(true);
      clone.querySelector('[data-testid="variant-option-single-value"]')?.remove();
      const name = normalize(clone.textContent);
      if (name) selected.set(name, value);
    });

    return selected;
  }

  function resolveVariant(product, picker) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (!variants.length) return null;

    const optionNames = Array.isArray(product?.options)
      ? product.options.map((option) => normalize(typeof option === 'string' ? option : option?.name))
      : [];
    const selected = getSelectedOptions(picker);

    const values = optionNames.map((name, index) => {
      if (name && selected.has(name)) return selected.get(name);
      const unique = [...new Set(variants.map((variant) => normalize(variant?.options?.[index])).filter(Boolean))];
      return unique.length === 1 ? unique[0] : '';
    });

    if (!values.length || values.some((value) => !value)) return null;
    return variants.find((variant) => values.every((value, index) => normalize(variant?.options?.[index]) === value)) || null;
  }

  async function loadProduct(picker) {
    const productUrl = getProductUrl(picker);
    if (!productUrl) return null;

    if (!productCache.has(productUrl)) {
      const request = fetch(`${productUrl}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Product lookup failed: ${response.status}`);
          return response.json();
        })
        .then((product) => {
          productCache.set(productUrl, product);
          return product;
        })
        .catch((error) => {
          productCache.delete(productUrl);
          console.warn('[variant-integrity] Native Shopify flow retained:', error);
          return null;
        });
      productCache.set(productUrl, request);
    }

    return productCache.get(productUrl);
  }

  function applyExactVariant(picker, variant) {
    if (!variant?.id) return;

    const root = getRoot(picker);
    const productId = getProductId(picker);
    const variantId = normalize(variant.id);
    const available = variant.available !== false;

    root.querySelectorAll('product-form-component').forEach((component) => {
      if (productId && normalize(component.dataset.productId) !== productId) return;

      const input = component.querySelector('input[name="id"][ref="variantId"], input[name="id"]');
      if (input instanceof HTMLInputElement && input.value !== variantId) {
        input.value = variantId;
        input.setAttribute('value', variantId);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      component.dataset.roResolvedVariantId = variantId;
      component.removeAttribute('data-ro-variant-resolving');

      component.querySelectorAll('[ref="addToCartButton"]').forEach((button) => {
        if (button instanceof HTMLButtonElement && !available) button.disabled = true;
      });

      const accelerated = component.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
      if (!available) accelerated?.setAttribute('hidden', 'true');
    });

    root.querySelectorAll('sticky-add-to-cart').forEach((bar) => {
      if (productId && normalize(bar.dataset.productId) !== productId) return;
      bar.dataset.currentVariantId = variantId;
      bar.dataset.variantAvailable = String(available);
    });
  }

  async function syncPicker(picker) {
    if (!(picker instanceof HTMLElement)) return;
    const product = await loadProduct(picker);
    if (!product) return;
    const variant = resolveVariant(product, picker);
    if (variant) applyExactVariant(picker, variant);
  }

  function primeAll(root = document) {
    root.querySelectorAll?.('variant-picker').forEach((picker) => syncPicker(picker));
  }

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.matches('input, select')) return;
    const picker = target.closest('variant-picker');
    if (picker) queueMicrotask(() => syncPicker(picker));
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => primeAll(), { once: true });
  } else {
    primeAll();
  }

  document.addEventListener('shopify:section:load', (event) => primeAll(event.target));
})();
